/**
 * HomePageForSupplier Hook
 * 협력사 홈페이지 데이터 관리 및 API 호출 로직
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUserInfo } from '../../../core/utils';
import {
    SupplierTabType,
    SupplierInfo,
    DateRange,
    WeeklyStats,
    DailyStats,
    RiderStats,
    DayData,
    TimeSlot,
    RiderData,
    AutoRefreshInterval,
    TimeSlotStatus,
    DayOfWeek,
    WeekOption,
    DateOption,
    DEFAULT_WEEKLY_STATS,
    DEFAULT_DAILY_STATS,
    DEFAULT_RIDER_STATS,
    DEFAULT_DAILY_DATA,
    DEFAULT_TIME_SLOTS,
    AUTO_REFRESH_OPTIONS,
    GRADE_OPTIONS,
    SET_COUNT_OPTIONS,
    RIDER_FILTER_OPTIONS,
    RIDER_SORT_OPTIONS,
    PEAK_TIME_RANGES,
} from '../types/homePageForSupplier';
import {
    saveSupplierSettings,
    getWeeklyScore,
    getWeeklyStatus,
    getDailyStatus,
    getRiderStatus,
    getSupplierInfo,
} from '../api/supplierHomeApi';

/**
 * 현재 날짜가 주말인지 확인
 */
const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0: 일요일, 6: 토요일
};

/**
 * 현재 시간대의 슬롯 상태 계산
 */
const calculateTimeSlotStatus = (slotId: string, now: Date): TimeSlotStatus => {
    const currentHour = now.getHours();
    const weekend = isWeekend(now);
    const timeType = weekend ? 'weekend' : 'weekday';

    const ranges = PEAK_TIME_RANGES[slotId as keyof typeof PEAK_TIME_RANGES];
    if (!ranges) return 'waiting';

    const range = ranges[timeType];

    // 심야 시간대 특별 처리 (20:00 ~ 02:59)
    if (slotId === 'night') {
        // 20시 이후이거나, 3시 전이면 진행중
        if (currentHour >= 20 || currentHour < 3) {
            return 'inProgress';
        }
        // 3시 이후 ~ 6시 전이면 완료 (아침 피크 전)
        if (currentHour >= 3 && currentHour < 6) {
            return 'completed';
        }
        // 그 외는 대기중
        return 'waiting';
    }

    // 현재 시간이 해당 슬롯 시간대에 포함되는지 확인
    if (currentHour >= range.start && currentHour < range.end) {
        return 'inProgress';
    }

    // 현재 시간이 해당 슬롯 시간대 이후인지 확인
    if (currentHour >= range.end) {
        return 'completed';
    }

    // 현재 시간이 해당 슬롯 시간대 이전
    return 'waiting';
};

/**
 * 날짜를 YYYY-MM-DD 형식으로 변환
 */
const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * 이번 달의 수~화 주간 옵션 생성
 */
const generateWeekOptions = (): WeekOption[] => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const options: WeekOption[] = [];

    // 이번 달 1일 찾기
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);

    // 이번 달 첫 번째 수요일 찾기
    const firstWednesday = new Date(firstDayOfMonth);
    const dayOfWeek = firstWednesday.getDay();
    // 수요일은 3
    const daysUntilWed = dayOfWeek <= 3 ? 3 - dayOfWeek : 10 - dayOfWeek;
    firstWednesday.setDate(firstWednesday.getDate() + daysUntilWed);

    // 첫 번째 수요일이 이번 달이 아니면 이전 달의 마지막 수요일부터 시작
    if (firstWednesday.getMonth() !== currentMonth) {
        firstWednesday.setDate(firstWednesday.getDate() - 7);
    }

    // 주간 옵션 생성 (수~화)
    const startDate = new Date(firstWednesday);

    while (startDate.getMonth() === currentMonth || options.length === 0) {
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6); // 화요일

        const startStr = formatDate(startDate);
        const endStr = formatDate(endDate);

        options.push({
            value: `${startStr} ~ ${endStr}`,
            label: `${startStr} ~ ${endStr}`,
            start: new Date(startDate),
            end: new Date(endDate),
        });

        // 다음 주 수요일로 이동
        startDate.setDate(startDate.getDate() + 7);

        // 다음 달로 넘어가면 중지 (단, 최소 1개는 생성)
        if (startDate.getMonth() !== currentMonth && options.length > 0) {
            break;
        }
    }

    return options;
};

/**
 * 이번 주 수요일부터 다음 주 화요일까지 날짜 옵션 생성
 */
const generateDateOptions = (): DateOption[] => {
    const now = new Date();
    const options: DateOption[] = [];

    // 이번 주 수요일 찾기
    const dayOfWeek = now.getDay();
    const thisWednesday = new Date(now);

    // 현재 요일에서 수요일까지의 차이 계산
    // 일(0), 월(1), 화(2)이면 이전 주 수요일로
    // 수(3)~토(6)이면 이번 주 수요일로
    if (dayOfWeek < 3) {
        // 일, 월, 화 -> 이전 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek + 4));
    } else {
        // 수, 목, 금, 토 -> 이번 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek - 3));
    }

    // 수요일부터 화요일까지 7일 생성
    for (let i = 0; i < 7; i++) {
        const date = new Date(thisWednesday);
        date.setDate(thisWednesday.getDate() + i);

        const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
        const dateStr = formatDate(date);
        const dayName = dayNames[date.getDay()];

        options.push({
            value: dateStr,
            label: `${dateStr} (${dayName})`,
        });
    }

    return options;
};

/**
 * 오늘의 요일 반환
 */
const getTodayDayOfWeek = (): DayOfWeek => {
    const dayMap: { [key: number]: DayOfWeek } = {
        0: 'sun',
        1: 'mon',
        2: 'tue',
        3: 'wed',
        4: 'thu',
        5: 'fri',
        6: 'sat',
    };
    return dayMap[new Date().getDay()];
};

/**
 * 주간 현황용 주차 옵션 타입
 */
export interface WeeklyStatusOption {
    value: string;          // "W47" 형식
    label: string;          // "W47 (24.11.20~24.11.26) 형식"
    startDate: string;
    endDate: string;
}

/**
 * 이번 주 ~ 8주 전까지의 주차 옵션 생성 (수~화 기준)
 */
const generateWeeklyStatusOptions = (): WeeklyStatusOption[] => {
    const options: WeeklyStatusOption[] = [];
    const now = new Date();

    // 이번 주 수요일 찾기
    const dayOfWeek = now.getDay();
    const thisWednesday = new Date(now);

    // 현재 요일에서 수요일까지의 차이 계산
    // 일(0), 월(1), 화(2)이면 이전 주 수요일로
    // 수(3)~토(6)이면 이번 주 수요일로
    if (dayOfWeek < 3) {
        // 일, 월, 화 -> 이전 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek + 4));
    } else {
        // 수, 목, 금, 토 -> 이번 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek - 3));
    }

    // 이번 주부터 8주 전까지 (총 9주)
    for (let i = 0; i < 9; i++) {
        const startDate = new Date(thisWednesday);
        startDate.setDate(thisWednesday.getDate() - (i * 7));

        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);   // 화요일

        // ISO 주차 계산
        const weekNumber = getWeekNumber(startDate);

        const startStr = formatDate(startDate);
        const endStr = formatDate(endDate);

        // 짧은 날짜 형식 (YY.MM.DD)
        const shortStart = `${String(startDate.getFullYear()).slice(2)}.${String(startDate.getMonth() + 1).padStart(2, '0')}.${String(startDate.getDate()).padStart(2, '0')}`;
        const shortEnd = `${String(endDate.getFullYear()).slice(2)}.${String(endDate.getMonth() + 1).padStart(2, '0')}.${String(endDate.getDate()).padStart(2, '0')}`;

        options.push({
            value: `W${weekNumber}`,
            label: `W${weekNumber} (${shortStart}~${shortEnd})`,
            startDate: startStr,
            endDate: endStr,
        });
    }

    return options;
};

/**
 * 주어진 날짜의 ISO 주차 계산
 */
const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

/**
 * 협력사 홈페이지 Hook
 */
export const useHomePageForSupplier = () => {
    // 사용자 정보
    const userInfo = useMemo(() => getUserInfo(), []);
    const adminViewSuppId = localStorage.getItem('ADMIN_VIEW_SUPP_ID');
    const suppId = useMemo(() => adminViewSuppId || userInfo?.suppId || '', [userInfo, adminViewSuppId]);

    // 협력사 정보 (API에서 suppName, slotSetCnt 가져옴)
    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo>({
        supplierName: userInfo?.userName || userInfo?.supplierName || '협력사',
    });

    // 협력사 슬롯 세트 수 (localStorage 캐시된 값 또는 기본값 1)
    const [slotSetCnt, setSlotSetCnt] = useState<number>(() => {
        const id = userInfo?.suppId || '';
        const cached = localStorage.getItem(`slotSetCnt_${id}`);
        return cached ? parseInt(cached, 10) : 1;
    });

    // 협력사 원래 슬롯 세트 수 (API에서 가져온 값, "미사용" 선택 시 복원용)
    const [originalSlotSetCnt, setOriginalSlotSetCnt] = useState<number>(() => {
        const id = userInfo?.suppId || '';
        const cached = localStorage.getItem(`slotSetCnt_${id}`);
        return cached ? parseInt(cached, 10) : 1;
    });

    // 협력사 정보 로드 완료 여부
    const [isSupplierInfoLoaded, setIsSupplierInfoLoaded] = useState<boolean>(false);

    // 탭 상태
    const [activeTab, setActiveTab] = useState<SupplierTabType>('settings');

    // 자동 갱신 상태
    const [autoRefreshInterval, setAutoRefreshInterval] = useState<AutoRefreshInterval>('off');
    const [showAutoRefreshSelect, setShowAutoRefreshSelect] = useState(false);
    const [lastUpdate, setLastUpdate] = useState('--:--');
    const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
    const refreshAllDataRef = useRef<() => Promise<void>>(() => Promise.resolve());

    // 공통 상태
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 주간 옵션 및 날짜 옵션 생성
    const weekOptions = useMemo(() => generateWeekOptions(), []);
    const dateOptions = useMemo(() => generateDateOptions(), []);
    const todayDayOfWeek = useMemo(() => getTodayDayOfWeek(), []);

    // 정보 설정 탭 상태
    const [dateRange, setDateRange] = useState<DateRange>(() => {
        const options = generateWeekOptions();
        if (options.length > 0) {
            return {
                start: formatDate(options[0].start),
                end: formatDate(options[0].end),
            };
        }
        return { start: '', end: '' };
    });
    const [setCount, setSetCount] = useState('미사용');
    const [grade, setGrade] = useState('미사용');

    // 주간 예상 점수 탭 상태
    const [weeklyStats, setWeeklyStats] = useState<WeeklyStats>(DEFAULT_WEEKLY_STATS);
    const [dailyData, setDailyData] = useState<DayData[]>(DEFAULT_DAILY_DATA);

    // 금일 달성현황 탭 상태
    const [selectedDate, setSelectedDate] = useState(() => {
        const options = generateDateOptions();
        // 오늘 날짜 찾기
        const today = formatDate(new Date());
        const todayOption = options.find(opt => opt.value === today);
        return todayOption ? todayOption.value : (options[0]?.value || '');
    });
    const [dailyStats, setDailyStats] = useState<DailyStats>(DEFAULT_DAILY_STATS);

    // 시간대별 슬롯 상태 (API에서 가져온 데이터 또는 기본값)
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(() =>  {
        const now = new Date();
        return DEFAULT_TIME_SLOTS.map(slot => ({
            ...slot,
            status: calculateTimeSlotStatus(slot.id, now),
        }));
    });

    // 라이더현황 탭 상태 (당일)
    const [riderFilter, setRiderFilter] = useState('전체');
    const [sortBy, setSortBy] = useState('수락율');
    const [searchQuery, setSearchQuery] = useState('');
    const [riderStats, setRiderStats] = useState<RiderStats>(DEFAULT_RIDER_STATS);
    const [riders, setRiders] = useState<RiderData[]>([]);

    // 주간 라이더현황 상태
    const [weeklyRiderStats, setWeeklyRiderStats] = useState<RiderStats>(DEFAULT_RIDER_STATS);
    const [weeklyRiders, setWeeklyRiders] = useState<RiderData[]>([]);

    // 주간 현황용 주차 선택 상태
    const weeklyStatusOptions = useMemo(() => generateWeeklyStatusOptions(), []);
    const [selectedWeeklyOption, setSelectedWeeklyOption] = useState<WeeklyStatusOption>(() => {
       const options = generateWeeklyStatusOptions();
       return options[0] || { value: '', label: '', startDate: '', endDate: '' };
    });

    /**
     * 세트수에 따른 슬롯 달성률 계산
     * - 세트수가 변경되면 totalSlots가 조정됨
     * - 화면 새로고침 시 원래 값으로 복원
     */
    const adjustedSlotStats = useMemo(() => {
        // 세트수에서 숫자 추출 (예: "3세트" -> 3)
        const setCountMatch = setCount.match(/(\d+)세트/);
        const setCountNum = setCountMatch ? parseInt(setCountMatch[1], 10) : 0;

        if (setCountNum === 0 || setCount === '미사용') {
            // 미사용이면 기본 weeklyStats 값 사용
            return {
                achievedSlots: weeklyStats.achievedSlots,
                totalSlots: weeklyStats.totalSlots,
                achievementRate: weeklyStats.totalSlots > 0
                    ? Math.round((weeklyStats.achievedSlots / weeklyStats.totalSlots) * 100)
                    : 0,
            };
        }

        // 세트수에 따른 총 슬롯 수 계산: 세트수 × 7일 × 4시간대
        const adjustedTotalSlots = 7 * 4;
        const achievedSlots = weeklyStats.achievedSlots;

        return {
            achievedSlots,
            totalSlots: adjustedTotalSlots,
            achievementRate: adjustedTotalSlots > 0
                ? Math.round((achievedSlots / adjustedTotalSlots) * 100)
                : 0,
        };
    }, [setCount, weeklyStats.achievedSlots, weeklyStats.totalSlots]);

    /**
     * 현재 시간 업데이트
     */
    const updateLastUpdateTime = useCallback(() => {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        setLastUpdate(`${hours}:${minutes}`);
    }, []);

    /**
     * 탭 변경 핸들러
     */
    const handleTabChange = useCallback((tab: SupplierTabType) => {
        setActiveTab(tab);
    }, []);

    /**
     * 자동 갱신 선택 토글
     */
    const handleAutoRefreshToggle = useCallback(() => {
        setShowAutoRefreshSelect(prev => !prev);
    }, []);

    /**
     * 자동 갱신 간격 변경
     */
    const handleAutoRefreshChange = useCallback((interval: AutoRefreshInterval) => {
        setAutoRefreshInterval(interval);
        setShowAutoRefreshSelect(false);

        // 기존 타이머 정리
        if (autoRefreshTimerRef.current) {
            clearInterval(autoRefreshTimerRef.current);
            autoRefreshTimerRef.current = null;
        }

        // 새 타이머 설정
        const option = AUTO_REFRESH_OPTIONS.find(opt => opt.value === interval);
        if (option && option.milliseconds > 0) {
            updateLastUpdateTime(); // 초기 시간 설정
            autoRefreshTimerRef.current = setInterval(() => {
                // ref를 통해 최신 refreshAllData 함수 호출
                refreshAllDataRef.current();
            }, option.milliseconds);
        }
    }, [updateLastUpdateTime]);

    /**
     * 날짜 범위 변경 핸들러
     */
    const handleDateRangeChange = useCallback((start: string, end: string) => {
        setDateRange({ start, end });
    }, []);

    /**
     * 세트수에 따른 등급 계산
     * 5세트 이상: 마스터
     * 3세트 이상: 다이아
     * 2세트 이상: 골드
     * 1세트 이상: 실버
     * 0세트 이상: 브론즈
     * 미사용: 미사용
     */
    const calculateGradeFromSetCount = useCallback((setCountValue: string): string => {
        if (setCountValue === '미사용') {
            return '미사용';
        }

        const match = setCountValue.match(/(\d+)세트/);
        const setCountNum = match ? parseInt(match[1], 10) : 0;

        if (setCountNum >= 5) return '마스터';
        if (setCountNum >= 3) return '다이아';
        if (setCountNum >= 2) return '골드';
        if (setCountNum >= 1) return '실버';
        return '브론즈';
    }, []);

    /**
     * 세트수 변경 핸들러
     * 세트수가 변경되면 등급도 자동으로 계산되어 설정됨
     * 또한 slotSetCnt도 업데이트되어 당일 현황의 목표 건수 변경
     */
    const handleSetCountChange = useCallback((value: string) => {
        setSetCount(value);
        const newGrade = calculateGradeFromSetCount(value);
        setGrade(newGrade);

        // 세트수에서 숫자 추출해서 slotSetCnt 업데이트
        // "3세트" -> 3, "미사용" -> 협력사 원래 세트 수 (originalSlotSetCnt)
        const match = value.match(/(\d+)세트/);
        const cnt = match ? parseInt(match[1], 10) : originalSlotSetCnt;
        setSlotSetCnt(cnt);
    }, [calculateGradeFromSetCount, originalSlotSetCnt]);

    /**
     * 등급 변경 핸들러
     */
    const handleGradeChange = useCallback((value: string) => {
        setGrade(value);
    }, []);

    /**
     * 조회일자 변경 핸들러
     */
    const handleSelectedDateChange = useCallback((date: string) => {
        setSelectedDate(date);
    }, []);

    /**
     * 라이더 필터 변경 핸들러
     */
    const handleRiderFilterChange = useCallback((filter: string) => {
        setRiderFilter(filter);
    }, []);

    /**
     * 정렬 기준 변경 핸들러
     */
    const handleSortByChange = useCallback((sort: string) => {
        setSortBy(sort);
    }, []);

    /**
     * 검색어 변경 핸들러
     */
    const handleSearchQueryChange = useCallback((query: string) => {
        setSearchQuery(query);
    }, []);

    /**
     * 주간 현황 주차 선택 변경 핸들러
     */
    const handleWeeklyOptionChange = useCallback((optionValue: string) => {
        const option = weeklyStatusOptions.find(opt => opt.value === optionValue);
        if (option) {
            setSelectedWeeklyOption(option);
        }
    }, [weeklyStatusOptions]);

    /**
     * 설정 저장
     */
    const handleSaveSettings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await saveSupplierSettings({
                dateRange,
                setCount,
                grade,
            });
            if (response.success) {
                alert('설정이 저장되었습니다.');
            } else {
                setError(response.message || '설정 저장에 실패했습니다.');
            }
        } catch (err) {
            setError('설정 저장 중 오류가 발생했습니다.');
            console.error('Failed to save settings:', err);
        } finally {
            setIsLoading(false);
        }
    }, [dateRange, setCount, grade]);

    /**
     * 주간 예상 점수 조회
     */
    const fetchWeeklyScore = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getWeeklyScore({
                startDate: dateRange.start,
                endDate: dateRange.end,
            });
            if (response.success) {
                setWeeklyStats(response.weeklyStats);
                setDailyData(response.dailyData);
            } else {
                setError(response.message || '주간 점수 조회에 실패했습니다.');
            }
        } catch (err) {
            setError('주간 점수 조회 중 오류가 발생했습니다.');
            console.error('Failed to fetch weekly score:', err);
        } finally {
            setIsLoading(false);
        }
    }, [dateRange]);

    /**
     * 금일 달성현황 조회
     * 백엔드 /api/v1/dashboard/summary API를 호출하여 당일 현황 데이터를 가져옴.
     * slotSetCnt를 전달하여 협력사별 슬롯 목표치를 동적으로 계산합니다.
     */
    const fetchDailyStatus = useCallback(async () => {
        if (!suppId) {
            setError('협력사 ID가 없습니다. 다시 로그인해주세요.');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await getDailyStatus({
                date: selectedDate,
                suppId,
                slotSetCnt,  // 협력사별 슬롯 세트 수 전달
            });
            if (response.success) {
                setDailyStats(response.dailyStats);
                if (response.timeSlots) {
                    setTimeSlots(response.timeSlots);
                }
            } else {
                setError(response.message || '금일 달성현황 조회에 실패했습니다.');
            }
        } catch (err) {
            setError('금일 달성현황 조회 중 오류가 발생했습니다.');
            console.error('Failed to fetch daily status:', err);
        } finally {
            setIsLoading(false);
        }
    }, [selectedDate, suppId, slotSetCnt]);

    /**
     * 백엔드 대시보드 API를 호출하여 라이더 현황을 조회.
     * 오늘 날짜 기준으로 라이더별 건수를 조회합니다.
     */
    const fetchRiderStatus = useCallback(async () => {
        if (!suppId) {
            setError('협력사 ID가 없습니다. 다시 로그인해주세요.');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            // 오늘 날짜를 YYYYMMDD 형식으로 변환 (라이더 현황은 오늘 건수만 조회)
            const today = new Date();
            const todayFormatted = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;

            const response = await getRiderStatus({
                filter: riderFilter,
                sortBy,
                searchQuery,
                suppId,
                fromDate: todayFormatted,
                toDate: todayFormatted,
            });
            if (response.success) {
                setRiderStats(response.riderStats);
                setRiders(response.riders);
            } else {
                setError(response.message || '라이더 현황 조회에 실패했습니다.');
            }
        } catch (err) {
            setError('라이더 현황 조회 중 오류가 발생했습니다.');
            console.error('Failed to fetch rider status:', err);
        } finally {
            setIsLoading(false);
        }
    }, [riderFilter, sortBy, searchQuery, suppId]);

    /**
     * 주간 현황 조회
     * 백엔드 /api/v1/dashboard/weekly API를 호출하여 주간 현황 데이터 가져옴.
     * selectedWeeklyOption에서 선택된 주차의 날짜를 사용합니다.
     */
    const fetchWeeklyStatus = useCallback(async () => {
        if (!suppId) {
            setError('협력사 ID가 없습니다. 다시 로그인해주세요.');
            return;
        }

        if (!selectedWeeklyOption.startDate || !selectedWeeklyOption.endDate) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
            const fromDate = selectedWeeklyOption.startDate.replace(/-/g, '');
            const toDate = selectedWeeklyOption.endDate.replace(/-/g, '');

            const response = await getWeeklyStatus({
                fromDate,
                toDate,
                suppId,
            });
            if (response.success) {
                setWeeklyStats(response.weeklyStats);
                setDailyData(response.dailyData);
            } else {
                setError(response.message || '주간 현황 조회에 실패했습니다.');
            }
        } catch (err) {
            setError('주간 현황 조회 중 오류가 발생했습니다.');
            console.error('Failed to fetch weekly status: ', err);
        } finally {
            setIsLoading(false);
        }
    }, [selectedWeeklyOption, suppId]);

    /**
     * 주간 라이더 현황 조회
     * 주간 기간 기준으로 라이더별 건수를 조회.
     * selectedWeeklyOption에서 선택된 주차의 날짜를 사용합니다.
     */
    const fetchWeeklyRiderStatus = useCallback(async () => {
        if (!suppId) {
            setError('협력사 ID가 없습니다. 다시 로그인해주세요.');
            return;
        }

        if (!selectedWeeklyOption.startDate || !selectedWeeklyOption.endDate) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
            const fromDate = selectedWeeklyOption.startDate.replace(/-/g, '');
            const toDate = selectedWeeklyOption.endDate.replace(/-/g, '');

            const response = await getRiderStatus({
                filter: riderFilter,
                sortBy,
                searchQuery,
                suppId,
                fromDate,
                toDate,
            });
            if (response.success) {
                setWeeklyRiderStats(response.riderStats);
                setWeeklyRiders(response.riders);
            } else {
                setError(response.message || '주간 라이더 현황 조회에 실패했습니다.');
            }
        } catch (err) {
            setError('주간 라이더 현황 조회 중 오류가 발생했습니다.');
            console.error('Failed to fetch weekly rider status: ', err);
        } finally {
            setIsLoading(false);
        }
    }, [selectedWeeklyOption, riderFilter, sortBy, searchQuery, suppId]);

    /**
     * 모든 데이터 새로고침 (자동 갱신용)
     * 화면에 표시되는 모든 데이터를 갱신합니다.
     */
    const refreshAllData = useCallback(async () => {
        updateLastUpdateTime();
        // 모든 API를 병렬로 호출하여 데이터 갱신
        await Promise.all([
            fetchDailyStatus(),
            fetchRiderStatus(),
            fetchWeeklyStatus(),
            fetchWeeklyRiderStatus(),
        ]);
    }, [fetchDailyStatus, fetchRiderStatus, fetchWeeklyStatus, fetchWeeklyRiderStatus, updateLastUpdateTime]);

    // refreshAllDataRef를 최신 함수로 업데이트
    useEffect(() => {
        refreshAllDataRef.current = refreshAllData;
    }, [refreshAllData]);

    /**
     * 데이터 새로고침 (탭별)
     */
    const refreshData = useCallback(async () => {
        updateLastUpdateTime();
        switch (activeTab) {
            case 'weeklyScore':
                await fetchWeeklyScore();
                break;
            case 'dailyStatus':
                await fetchDailyStatus();
                break;
            case 'riderStatus':
                await fetchRiderStatus();
                break;
            default:
                break;
        }
    }, [activeTab, fetchWeeklyScore, fetchDailyStatus, fetchRiderStatus, updateLastUpdateTime]);

    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (autoRefreshTimerRef.current) {
                clearInterval(autoRefreshTimerRef.current);
            }
        };
    }, []);

    // 협력사 정보 조회 (suppName, slotSetCnt 가져오기)
    useEffect(() => {
        const fetchSupplierInfo = async () => {
            if (!suppId) return;

            const response = await getSupplierInfo(suppId);
            if (response.success) {
                if (response.suppName) {
                    setSupplierInfo({
                        supplierName: response.suppName,
                    });
                }
                // 슬롯 세트 수 설정
                setSlotSetCnt(response.slotSetCnt);
                // 원래 슬롯 세트 수 저장 ("미사용" 선택 시 복원)
                setOriginalSlotSetCnt(response.slotSetCnt);
                // localStorage에 캐시 (새로고침 시 사용)
                localStorage.setItem(`slotSetCnt_${suppId}`, String(response.slotSetCnt));
                // 협력사 정보 로드 완료 표시
                setIsSupplierInfoLoaded(true);
            }
        };

        fetchSupplierInfo();
    }, [suppId]);

    // 주차 변경 시 주간 현황 데이터 자동 조회
    useEffect(() => {
        if (selectedWeeklyOption.startDate && selectedWeeklyOption.endDate && suppId) {
            fetchWeeklyStatus();
            fetchWeeklyRiderStatus();
        }
    }, [selectedWeeklyOption, suppId, fetchWeeklyStatus, fetchWeeklyRiderStatus]);

    // slotSetCnt 변경 시 당일 현황 자동 조회 (협력사 정보 로드 후에만)
    useEffect(() => {
        // 협력사 정보가 로드되기 전에는 API 호출 안함 (새로고침 시 1세트 값으로 호출 방지)
        if (suppId && slotSetCnt > 0 && isSupplierInfoLoaded) {
            fetchDailyStatus();
        }
    }, [slotSetCnt, suppId, isSupplierInfoLoaded, fetchDailyStatus]);

    // 조회일자 변경 시 당일 현황 자동 조회 (협력사 정보 로드 후에만)
    useEffect(() => {
        // 협력사 정보가 로드되기 전에는 API 호출 안함
        if (suppId && selectedDate && isSupplierInfoLoaded) {
            fetchDailyStatus();
        }
    }, [selectedDate, suppId, isSupplierInfoLoaded, fetchDailyStatus]);

    return {
        // 사용자 정보
        supplierInfo,
        slotSetCnt,  // 협력사 슬롯 세트 수

        // 탭 상태
        activeTab,
        handleTabChange,

        // 자동 갱신 상태
        autoRefreshInterval,
        showAutoRefreshSelect,
        lastUpdate,
        handleAutoRefreshToggle,
        handleAutoRefreshChange,
        refreshData,

        // 공통 상태
        isLoading,
        error,

        // 옵션 목록
        weekOptions,
        dateOptions,
        todayDayOfWeek,
        gradeOptions: GRADE_OPTIONS,
        setCountOptions: SET_COUNT_OPTIONS,
        riderFilterOptions: RIDER_FILTER_OPTIONS,
        riderSortOptions: RIDER_SORT_OPTIONS,
        autoRefreshOptions: AUTO_REFRESH_OPTIONS,

        // 정보 설정 탭
        dateRange,
        setCount,
        grade,
        handleDateRangeChange,
        handleSetCountChange,
        handleGradeChange,
        handleSaveSettings,

        // 주간 현황 탭
        weeklyStats,
        adjustedSlotStats,
        dailyData,
        fetchWeeklyScore,
        fetchWeeklyStatus,
        weeklyStatusOptions,
        selectedWeeklyOption,
        handleWeeklyOptionChange,

        // 금일 달성현황 탭
        selectedDate,
        dailyStats,
        timeSlots,
        handleSelectedDateChange,
        fetchDailyStatus,

        // 라이더현황 탭 (당일)
        riderFilter,
        sortBy,
        searchQuery,
        riderStats,
        riders,
        handleRiderFilterChange,
        handleSortByChange,
        handleSearchQueryChange,
        fetchRiderStatus,

        // 주간 라이더현황
        weeklyRiderStats,
        weeklyRiders,
        fetchWeeklyRiderStatus,
    };
};
